import CircleWithLabels from "@ihs-widget/components/StatsConsole/Widgets/StatsWidget/parts/CircleWithLabels";
import useTimeoutVisability from "@ihs-widget/utils/hooks/useTimeoutVisability";

interface TripleCircleStatisticProps {
    label_1: string;
    home_1: number;
    away_1: number;
    isPercentage_1: boolean;
    label_2: string;
    home_2: number;
    away_2: number;
    isPercentage_2: boolean;
    label_3: string;
    home_3: number;
    away_3: number;
    isPercentage_3: boolean;
    mt?: string;
    mb?: string;
}

const TripleCircleStatistic = ({ label_1, home_1, away_1, isPercentage_1, label_2, home_2, away_2, isPercentage_2, label_3, home_3, away_3, isPercentage_3, mt = 'mt-[24px]', mb = 'mb-0' }: TripleCircleStatisticProps) => {
    // Check if pair values are null
    const firstPairNull = home_1 === null && away_1 === null;
    const secondPairNull = home_2 === null && away_2 === null;
    const thirdPairNull = home_3 === null && away_3 === null;
    const allValuesNull = firstPairNull && secondPairNull && thirdPairNull;

    // Timeout logic for first pair
    const { isVisible: isFirstVisible } = useTimeoutVisability({
        timeout: 5000,
        dependencies: [firstPairNull, home_1, away_1]
    });

    // Timeout logic for second pair
    const { isVisible: isSecondVisible } = useTimeoutVisability({
        timeout: 5000,
        dependencies: [secondPairNull, home_2, away_2]
    });

    // Timeout logic for third pair
    const { isVisible: isThirdVisible } = useTimeoutVisability({
        timeout: 5000,
        dependencies: [thirdPairNull, home_3, away_3]
    });

    // Timeout logic for all values
    const { isVisible: isComponentVisible } = useTimeoutVisability({
        timeout: 5000,
        dependencies: [allValuesNull, home_1, away_1, home_2, away_2, home_3, away_3]
    });

    // Hide all component if all values are null for 5 seconds
    if (allValuesNull && !isComponentVisible) {
        return null;
    }

    // Calculate which components should be shown
    const showFirst = !firstPairNull || isFirstVisible;
    const showSecond = !secondPairNull || isSecondVisible;
    const showThird = !thirdPairNull || isThirdVisible;

    const visibleComponents = [showFirst, showSecond, showThird].filter(Boolean).length;

    // If only one component is visible, center it
    if (visibleComponents === 1) {
        return (
            <div className={`w-full min-h-[65px] h-[65px] max-h-[65px] flex items-center justify-center flex-row ${mt} ${mb ? mb : 'mb-0'}`}>
                {showFirst && <CircleWithLabels label={label_1} home={home_1} away={away_1} />}
                {showSecond && <CircleWithLabels label={label_2} home={home_2} away={away_2} />}
                {showThird && <CircleWithLabels label={label_3} home={home_3} away={away_3} />}
            </div>
        );
    }

    // If two components are visible, distribute them evenly
    if (visibleComponents === 2) {
        return (
            <div className={`w-full min-h-[65px] h-[65px] max-h-[65px] flex items-center justify-between flex-row ${mt} gap-[8px] ${mb ? mb : 'mb-0'}`}>
                {showFirst && <CircleWithLabels label={label_1} home={home_1} away={away_1} />}
                {showSecond && <CircleWithLabels label={label_2} home={home_2} away={away_2} />}
                {showThird && <CircleWithLabels label={label_3} home={home_3} away={away_3} />}
            </div>
        );
    }

    // Standard view with three elements
    return (
        <div className={`w-full min-h-[65px] h-[65px] max-h-[65px] flex items-center justify-between flex-row ${mt} gap-[8px] ${mb ? mb : 'mb-0'}`}>
            <CircleWithLabels label={label_1} home={home_1} away={away_1} />
            <CircleWithLabels label={label_2} home={home_2} away={away_2} />
            <CircleWithLabels label={label_3} home={home_3} away={away_3} />
        </div>
    )
}

export default TripleCircleStatistic;